import {
  skipToken,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  EDI844Submission,
  EDI844SubmissionLine,
  EDI844SubmissionActivity,
  ListEDI844SubmissionsInput,
  ListEDI844SubmissionsResponse,
  GetEDI844SubmissionInput,
  GetEDI844SubmissionResponse,
  CreateEDI844SubmissionInput,
  UpdateEDI844SubmissionInput,
  UpdateEDI844SubmissionLineInput,
  BatchUpdateEDI844SubmissionLinesInput,
  ListEDI844SubmissionActivitiesInput,
  ListEDI844SubmissionActivitiesResponse,
  AddEDI844SubmissionCommentInput,
  GetEDI844BatchGroupsInput,
  GetEDI844BatchGroupsResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook for listing EDI844 submissions with optional filters
 */
export function useEDI844SubmissionsList(
  filters: ListEDI844SubmissionsInput = {},
) {
  const query = useQuery(
    orpc.shipAndDebit.edi844Submissions.list.queryOptions({
      input: filters,
    }),
  );

  const submissions = query.data?.submissions ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    submissions,
    pagination,
  } satisfies typeof query & {
    data?: ListEDI844SubmissionsResponse;
    submissions: EDI844Submission[];
    pagination: ListEDI844SubmissionsResponse['pagination'] | undefined;
  };
}

/**
 * Hook for fetching a single EDI844 submission by ID
 */
export function useEDI844Submission(
  id: string | undefined,
  options: Omit<GetEDI844SubmissionInput, 'id'> = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.shipAndDebit.edi844Submissions.get.queryOptions({
      input: id ? { id, ...options } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    submission: query.data ?? null,
  } satisfies typeof query & {
    data?: GetEDI844SubmissionResponse;
    submission: GetEDI844SubmissionResponse | null;
  };
}

/**
 * Hook for creating a new EDI844 submission
 */
export function useCreateEDI844Submission() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: CreateEDI844SubmissionInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.create(input);
    },
    onSuccess: () => {
      // Invalidate submissions list to refetch
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions', 'list'],
      });
      showToast({
        message: 'Submission created successfully',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to create submission',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for updating an EDI844 submission
 */
export function useUpdateEDI844Submission() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: UpdateEDI844SubmissionInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.update(input);
    },
    onSuccess: (data) => {
      // Invalidate both list and specific submission
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions', 'get', data.id],
      });
      showToast({
        message: 'Submission updated successfully',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to update submission',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for updating a submission line
 */
export function useUpdateEDI844SubmissionLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateEDI844SubmissionLineInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.updateLine(input);
    },
    onSuccess: () => {
      // Invalidate submissions to refetch with updated line data
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions'],
      });
    },
  });
}

/**
 * Hook for batch updating submission lines (e.g., processing 849 responses)
 */
export function useBatchUpdateEDI844SubmissionLines() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: BatchUpdateEDI844SubmissionLinesInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.batchUpdateLines(input);
    },
    onSuccess: (data) => {
      // Invalidate submissions to refetch
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions'],
      });
      showToast({
        message: `Updated ${data.updatedLines.length} lines`,
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to update lines',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for listing submission activities (timeline)
 */
export function useEDI844SubmissionActivities(
  submissionId: string | undefined,
  filters: Omit<ListEDI844SubmissionActivitiesInput, 'submissionId'> = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.shipAndDebit.edi844Submissions.listActivities.queryOptions({
      input: submissionId ? { submissionId, ...filters } : skipToken,
    }),
    enabled: Boolean(submissionId) && enabled,
  });

  const activities = query.data?.activities ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    activities,
    pagination,
  } satisfies typeof query & {
    data?: ListEDI844SubmissionActivitiesResponse;
    activities: EDI844SubmissionActivity[];
    pagination:
      | ListEDI844SubmissionActivitiesResponse['pagination']
      | undefined;
  };
}

/**
 * Hook for adding a comment to a submission
 */
export function useAddEDI844SubmissionComment() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: AddEDI844SubmissionCommentInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.addComment(input);
    },
    onSuccess: (_, variables) => {
      // Invalidate activities for this submission
      queryClient.invalidateQueries({
        queryKey: [
          'shipAndDebit',
          'edi844Submissions',
          'listActivities',
          variables.submissionId,
        ],
      });
      showToast({
        message: 'Comment added',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to add comment',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for getting batch groups (for creating new submissions)
 */
export function useEDI844BatchGroups(
  filters: GetEDI844BatchGroupsInput = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.shipAndDebit.edi844Submissions.getBatchGroups.queryOptions({
      input: filters,
    }),
    enabled,
  });

  const groups = query.data?.groups ?? [];

  return {
    ...query,
    groups,
  } satisfies typeof query & {
    data?: GetEDI844BatchGroupsResponse;
    groups: GetEDI844BatchGroupsResponse['groups'];
  };
}
